import {
  activateKakaoTalkChannel as activateKakaoTalkChannelApi,
  deactivateKakaoTalkChannel as deactivateKakaoTalkChannelApi,
  getKakaoTalkChannel,
  registerKakaoTalkChannel as registerKakaoTalkChannelApi,
  verifyKakaoTalkChannel,
} from '@modusign/openapi-client-notification';
import {
  GetKakaoTalkChannelResponseDto,
  RegisterKakaoTalkChannelResponseDto,
  VerifyKakaoTalkChannelResponseDto,
} from '@modusign/openapi-client-notification/generated/model';
import { all, call, put, takeLatest, takeLeading } from 'redux-saga/effects';

import workspaceService from '@app/libs/workspaceService';
import {
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_ACTIVATE_REQUEST,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_ACTIVATE_SUCCESS,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_AUTHENTICATE_REQUEST,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_AUTHENTICATE_SUCCESS,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_BUSINESS_CATEGORIES_FETCH_REQUEST,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_DEACTIVATE_REQUEST,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_DEACTIVATE_SUCCESS,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_FETCH_REQUEST,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_REGISTER_REQUEST,
} from '@app/store/actionTypes';
import {
  getBusinessCategories,
  GetBusinessCategoryResponse,
} from '@app/store/transport-layer/notification/kakao-channel/business-categories';

import {
  activateKakaoTalkChannel,
  activateKakaoTalkChannelFailure,
  activateKakaoTalkChannelSuccess,
  authenticateKakaoTalkChannel,
  authenticateKakaoTalkChannelFailure,
  authenticateKakaoTalkChannelSuccess,
  deactivateKakaoTalkChannel,
  deactivateKakaoTalkChannelFailure,
  deactivateKakaoTalkChannelSuccess,
  fetchKakaoTalkBusinessCategoriesFailure,
  fetchKakaoTalkBusinessCategoriesSuccess,
  fetchKakaoTalkChannelFailure,
  fetchKakaoTalkChannelSuccess,
  registerKakaoTalkChannel,
  registerKakaoTalkChannelFailure,
  registerKakaoTalkChannelSuccess,
} from './state';
import KakaoTalkChannel from './types/KakaoTalkChannel';

export function* activateKakaoTalkChannelSaga({
  payload: { plusFriendId },
}: ReturnType<typeof activateKakaoTalkChannel>) {
  try {
    yield call(activateKakaoTalkChannelApi, workspaceService.getWorkspaceId(), {
      plusFriendId,
    });
    yield put(activateKakaoTalkChannelSuccess());
  } catch (error) {
    yield put(activateKakaoTalkChannelFailure(error));
  }
}

export function* authenticateKakaoTalkChannelSaga({
  payload: { authenticationCode, categoryCode, plusFriendId },
}: ReturnType<typeof authenticateKakaoTalkChannel>) {
  try {
    const { isSuccessful, resultMessage }: VerifyKakaoTalkChannelResponseDto = yield call(
      verifyKakaoTalkChannel,
      workspaceService.getWorkspaceId(),
      {
        token: authenticationCode,
        categoryCode,
        plusFriendId,
      }
    );
    if (isSuccessful) {
      yield put(authenticateKakaoTalkChannelSuccess());
    } else {
      yield put(authenticateKakaoTalkChannelFailure(new Error(resultMessage)));
    }
  } catch (error) {
    yield put(authenticateKakaoTalkChannelFailure(error));
  }
}

export function* deactivateKakaoTalkChannelSaga({
  payload: { plusFriendId },
}: ReturnType<typeof deactivateKakaoTalkChannel>) {
  try {
    yield call(deactivateKakaoTalkChannelApi, workspaceService.getWorkspaceId(), {
      plusFriendId,
    });
    yield put(deactivateKakaoTalkChannelSuccess());
  } catch (error) {
    yield put(deactivateKakaoTalkChannelFailure(error));
  }
}

export function* fetchKakaoTalkBusinessCategorySaga() {
  try {
    const { categories }: GetBusinessCategoryResponse = yield call(getBusinessCategories);
    yield put(fetchKakaoTalkBusinessCategoriesSuccess(categories));
  } catch (error) {
    yield put(fetchKakaoTalkBusinessCategoriesFailure(error));
  }
}

export function* fetchKakaoTalkChannelSaga() {
  try {
    const { kakaoTalkChannel }: GetKakaoTalkChannelResponseDto = yield call(
      getKakaoTalkChannel,
      workspaceService.getWorkspaceId()
    );
    const payload: KakaoTalkChannel | null = kakaoTalkChannel
      ? {
          id: kakaoTalkChannel.id,
          plusFriendId: kakaoTalkChannel.plusFriendId,
          isActivated: kakaoTalkChannel.kakaoTalkCustomChannel.isActivated,
        }
      : null;
    yield put(fetchKakaoTalkChannelSuccess(payload));
  } catch (error) {
    yield put(fetchKakaoTalkChannelFailure(error));
  }
}

export function* registerKakaoTalkChannelSaga({
  payload: { categoryCode, phoneNumber, plusFriendId },
}: ReturnType<typeof registerKakaoTalkChannel>) {
  try {
    const { isSuccessful, resultMessage }: RegisterKakaoTalkChannelResponseDto = yield call(
      registerKakaoTalkChannelApi,
      workspaceService.getWorkspaceId(),
      {
        categoryCode,
        phoneNumber,
        plusFriendId,
      }
    );
    if (isSuccessful) {
      yield put(registerKakaoTalkChannelSuccess());
    } else {
      yield put(registerKakaoTalkChannelFailure(new Error(resultMessage)));
    }
  } catch (error) {
    yield put(registerKakaoTalkChannelFailure(error));
  }
}

export default function* customEmailSettingSaga() {
  yield all([
    takeLeading(
      CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_ACTIVATE_REQUEST,
      activateKakaoTalkChannelSaga
    ),
    takeLeading(
      CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_AUTHENTICATE_REQUEST,
      authenticateKakaoTalkChannelSaga
    ),
    takeLeading(
      CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_DEACTIVATE_REQUEST,
      deactivateKakaoTalkChannelSaga
    ),
    takeLatest(
      CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_BUSINESS_CATEGORIES_FETCH_REQUEST,
      fetchKakaoTalkBusinessCategorySaga
    ),
    takeLatest(
      [
        CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_ACTIVATE_SUCCESS,
        CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_AUTHENTICATE_SUCCESS,
        CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_DEACTIVATE_SUCCESS,
        CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_FETCH_REQUEST,
      ],
      fetchKakaoTalkChannelSaga
    ),
    takeLeading(
      CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_REGISTER_REQUEST,
      registerKakaoTalkChannelSaga
    ),
  ]);
}
